import React, { useState } from 'react';
import { toast } from 'sonner';
import { X } from '@phosphor-icons/react';
import { adminAPI } from '../../lib/api';

/**
 * Admin-only dialog for creating a manual member/player who hasn't signed
 * up yet. Used inside the Men's Night editor for Wednesday-night roster
 * additions when a real human can't (or won't) self-register in time.
 *
 * On success calls `onCreated(profile)` with the freshly created profile so
 * the parent can immediately register them for the current session as a
 * player or sub.
 */
export default function ManualPlayerDialog({ open, onClose, onCreated }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [rating, setRating] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setRating('');
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('First and last name are required.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      };
      if (email.trim()) payload.email = email.trim();
      if (phone.trim()) payload.phone = phone.trim();
      const r = parseFloat(rating);
      if (!Number.isNaN(r) && rating.trim()) payload.usta_rating = r;
      const res = await adminAPI.createManualPlayer(payload);
      toast.success(`${res.data.name} created.`);
      reset();
      onCreated?.(res.data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create manual player.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="manual-player-overlay"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-5 space-y-4"
        data-testid="manual-player-dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-xl text-[#013523]">Create manual player</h3>
            <p className="text-xs text-[#6a766e] mt-1">
              For players who haven&apos;t signed up yet. They&apos;ll be added as
              approved members so you can include them in this session. They can
              claim and log into the account later.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#6a766e] hover:text-[#013523]"
            data-testid="manual-player-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="First name *">
            <input
              autoFocus
              data-testid="manual-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              className="w-full px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm"
            />
          </Field>
          <Field label="Last name *">
            <input
              data-testid="manual-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              className="w-full px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm"
            />
          </Field>
        </div>

        <Field label="Email (optional)" hint="Leave blank if unknown. Required later when they want to claim their account.">
          <input
            data-testid="manual-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone (optional)">
            <input
              data-testid="manual-phone"
              type="tel"
              placeholder="(949) 555-0123"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm"
            />
          </Field>
          <Field label="USTA rating (optional)">
            <input
              data-testid="manual-rating"
              type="number"
              step="0.5"
              min="1.0"
              max="7.0"
              placeholder="e.g. 4.0"
              value={rating}
              onChange={(e) => setRating(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm"
            />
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="manual-cancel"
            className="px-3 py-2 rounded-lg text-sm border border-[#e3dcd1] text-[#6a766e] hover:bg-[#e3dcd1]/40 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="manual-submit"
            className="px-4 py-2 rounded-lg text-sm bg-[#015e3f] text-white font-medium hover:bg-[#013523] disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create & add to session'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-[#6a766e] mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-[#6a766e] mt-1">{hint}</span>}
    </label>
  );
}
